import React, { useCallback, useState } from 'react';

import { Text } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import {
  initialErrorState,
  initialPaymentState,
  testCardPaymentState,
} from 'features/payment/initial-state';
import { CardType } from 'features/payment/types';
import {
  getCCFormErrors,
  mapDeliveryToCCFormAddress,
  mapPaymentMethodCardTypes,
  onCCFormChange,
} from 'features/payment/utils';
import { capitalize } from 'lodash-es';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { SafetechEncryption } from 'components/payments/integrations/orbital/components/encryption';
import {
  IEncryptionError,
  IEncryptionResult,
} from 'components/payments/integrations/orbital/components/encryption/types';
import { toaster } from 'components/toaster/toaster';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import CreditCardFormInputs, {
  getErrorMessage,
} from 'experiments/checkout-v2/control/components/credit-card-form-inputs';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import logger from 'utils/logger';
import { stringToBool } from 'utils/string-To-Bool';

export const AddPaymentMethodModal: React.FC = () => {
  const { user } = useAuthContext();
  const [errors, setErrors] = useState(initialErrorState);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    addPaymentMethod,
    transformPaymentValues,
    isOrbital,
    setEncryptionResult,
    isQuickPayMethodSelected,
    setIsQuickPayMethodSelected,
    setIsAddPaymentMethodSuccess,
  } = usePaymentContext();
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;

  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const { deliveryAddress } = useOrderContext();
  const { isDelivery: isDeliveryServiceMode } = useServiceModeContext();

  const router = useRouter();

  const { accountToDelete, isInCart } = useLocalSearchParams<{
    isInCart: string;
    accountToDelete: string;
  }>();
  const isDelivery = stringToBool(isInCart) && isDeliveryServiceMode;
  const { feCountryCode: billingCountry } = useLocale();
  const userDetailsName = user?.details.name;

  const [paymentValues, setPaymentValues] = useState(
    initialPaymentState({
      isDelivery,
      deliveryAddress,
      billingCountry,
      userDetailsName,
    })
  );
  const { formatMessage } = useIntl();
  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Credit Card details',
  });
  const [isEncryptionActive, setIsEncryptionActive] = useState(false);

  useEffectOnce(() => {
    if (autoFill) {
      setPaymentValues(testCardPaymentState({ isDelivery, paymentFieldVariations }));
    }
  });

  const handleChange = useCallback(
    (name: any, value: any) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        paymentValues,
        errors,
        formatMessage,
        supportedCardTypes
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setPaymentValues(prevState => {
        return state.billingAddressSameAsDelivery
          ? {
              ...prevState,
              ...state,
              ...mapDeliveryToCCFormAddress(deliveryAddress),
            }
          : { ...prevState, ...state };
      });
    },
    [paymentValues, errors, formatMessage, deliveryAddress, supportedCardTypes]
  );

  const addNewCard = useCallback(
    async (event: any, encryptionResult?: IEncryptionResult) => {
      if (event) {
        event.preventDefault();
      }

      const onAddCardSuccess = () => {
        toaster.positive(formatMessage({ id: 'yourNewPaymentMethodHasBeenSuccessfullySaved' }));

        setPaymentValues(initialPaymentState({ isDelivery, deliveryAddress }));
        if (isQuickPayMethodSelected) {
          setIsAddPaymentMethodSuccess(true);
          setIsQuickPayMethodSelected(false);
        }

        router.dismiss();
        setIsLoading(false);
      };

      const onAddCardFailure = (paymentMethodError: Error, message: string) => {
        logger.error({ error: paymentMethodError, message });
        openErrorDialog({
          message: formatMessage({ id: 'paymentAddingError' }),
          error: paymentMethodError,
        });
        setIsLoading(false);
      };

      setIsLoading(true);
      const reshapedPaymentValues = transformPaymentValues({
        paymentValues,
        paymentFieldVariations,
      });
      reshapedPaymentValues.accountToDelete = accountToDelete;

      try {
        await addPaymentMethod(
          reshapedPaymentValues,
          {
            skipErrorDialogOnError: true,
          },
          encryptionResult
        );
        onAddCardSuccess();
      } catch (error) {
        onAddCardFailure(error, 'Error adding payment method');
      }
    },
    [
      transformPaymentValues,
      paymentValues,
      paymentFieldVariations,
      accountToDelete,
      formatMessage,
      isDelivery,
      deliveryAddress,
      isQuickPayMethodSelected,
      router,
      setIsAddPaymentMethodSuccess,
      setIsQuickPayMethodSelected,
      openErrorDialog,
      addPaymentMethod,
    ]
  );

  const handleOnPress = useCallback(
    async (event: any) => {
      if (event) {
        event.preventDefault();
      }
      const { hasErrors, formErrors } = getCCFormErrors(
        paymentValues,
        errors,
        formatMessage,
        paymentFieldVariations,
        billingCountry
      );

      if (hasErrors) {
        const newErrorMessage = getErrorMessage({ errors, formatMessage });
        setErrorMessage(newErrorMessage);

        return setErrors(prevState => ({ ...prevState, ...formErrors, didAttemptSubmit: true }));
      }

      if (isOrbital) {
        setEncryptionResult(undefined);
        setIsLoading(true);
        return setIsEncryptionActive(true);
      }
      addNewCard(null);
    },
    [
      addNewCard,
      billingCountry,
      errors,
      formatMessage,
      isOrbital,
      paymentFieldVariations,
      paymentValues,
      setEncryptionResult,
    ]
  );

  const modalBtnText = `${
    accountToDelete ? capitalize(formatMessage({ id: 'confirm' })) : formatMessage({ id: 'save' })
  } & ${formatMessage({ id: 'continue' })}`;

  const onEncryptionResult = (data: IEncryptionResult) => {
    setEncryptionResult(data);
    setIsEncryptionActive(false);
    addNewCard(null, data);
  };

  // Handle encryption error
  const onEncryptionError = (error: IEncryptionError) => {
    setIsEncryptionActive(false);
    setEncryptionResult(undefined);
    setIsLoading(false);
    const errorMessage = error.error;
    logger.error({ error: new Error(errorMessage), message: errorMessage });
    openErrorDialog({
      message: formatMessage({ id: 'paymentAddingError' }),
      error: new Error(errorMessage),
    });
  };

  return (
    <CardModal
      testID="add-payment-modal-heading"
      standardPadding
      scrollView
      analyticsEvent="Add payment method"
      headerText={
        accountToDelete
          ? formatMessage({ id: 'updatePaymentMethod' })
          : formatMessage({ id: 'addPaymentMethod' })
      }
      footer={
        <ActionButton
          fullWidth
          testID="save-and-continue"
          isLoading={isLoading}
          disabled={!paymentValues.saveCard}
          onPress={handleOnPress}
        >
          {modalBtnText}
        </ActionButton>
      }
    >
      {!!errorMessage && (
        <VisuallyHidden role="alert" accessibilityLabel="">
          <Text>{errorMessage}</Text>
        </VisuallyHidden>
      )}
      <CreditCardFormInputs
        onChange={handleChange}
        paymentValues={paymentValues}
        errors={errors}
        isDelivery={isDelivery}
      />
      <ErrorDialog />

      {isOrbital && isEncryptionActive ? (
        <SafetechEncryption
          cardNumber={paymentValues.cardNumber}
          cvv={paymentValues.cvv || ''}
          onResult={onEncryptionResult}
          onError={onEncryptionError}
        />
      ) : null}
    </CardModal>
  );
};
